/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/sidebarMenu.scss';

import React, {MouseEventHandler, ReactElement, ReactNode, useContext} from 'react';

import {useTranslation} from 'react-i18next';

import classNames from 'classnames';

import {IconName} from '@fortawesome/fontawesome-svg-core';

import {AwesomeIcon} from '@dvrd/dvr-controls';

import {BluemateSidebarItem} from '../../util/interfaces';

import {isMobileReadyRoute} from '../../util/constants';

import {useBreakpoint} from '../../util/useBreakpoint';

import {LayoutContext} from '../misc/layoutContext';

import {useNotificationBadges} from '../misc/notificationBadgeContext';

import {BluemateBadge} from '../controls';

interface Props {
    onClickItem: (item: BluemateSidebarItem) => MouseEventHandler;
    items: BluemateSidebarItem[];
    numOpenRequests: number;
    activeItems: Array<string>;
}

export default function SidebarMenu(props: Props) {
    const {t} = useTranslation();
    const {isMobileLayout} = useBreakpoint();
    const {sidebarRef, menuCollapsed} = useContext(LayoutContext);
    const {items, onClickItem, numOpenRequests, activeItems} = props;
    const badgeCounts = useNotificationBadges();

    function renderItem(isChild: boolean = false) {
        return function (item: BluemateSidebarItem) {
            const {className, icon, id, children, label} = item;

            const isActive = activeItems.includes(item.id);
            const isOpen = item.alwaysOpen || isActive;
            const cls = classNames(
                className,
                'side-bar-item',
                item.disabled && 'disabled',
                isChild && 'child',
                children !== undefined && 'with-children'
            );
            const routeKey = typeof item.route === 'string' ? item.route : null;
            const unreadCount = routeKey ? badgeCounts[routeKey] : 0;
            return (
                <div key={id} className={cls} onClick={onClickItem(item)}>
                    {renderIcon(isActive, isChild, label, icon as ReactElement<any>)}
                    {renderItemLabel(isActive, isChild, item)}
                    {renderItemLabel(isActive, isChild, item, true)}
                    {!!unreadCount && (
                        <BluemateBadge
                            label={unreadCount > 99 ? '99+' : String(unreadCount)}
                            color='red'
                            size='small'
                            className='side-bar-unread-badge'
                        />
                    )}
                    {children !== undefined && (
                        <>
                            {isOpen && <div className={classNames('line', isActive && 'active')} />}
                            {renderChildren(isOpen)(children)}
                        </>
                    )}
                </div>
            );
        };
    }

    function renderItemLabel(
        isActive: boolean,
        isChild: boolean,
        item: BluemateSidebarItem,
        invisible: boolean = false
    ) {
        const {label, subText} = item;
        let subLabel: string | null = null;
        if (item.disabled) subLabel = t('sidebarMenu.comingSoon', '(coming soon)');
        else if (item.id === 'invoice-requests')
            subLabel = `(${numOpenRequests === -1 ? '-' : numOpenRequests > 100 ? '100+' : numOpenRequests})`;
        return (
            <label
                className={classNames('item-label', isActive && 'active', isChild && 'child', invisible && 'invisible')}
            >
                {label}
                {!!subLabel && <span className='item-sub-label'>&nbsp;{subLabel}</span>}
                {!!subText && (typeof subText === 'string' ? <span className='sub-label'>{subText}</span> : subText)}
                {isMobileLayout && item.children === undefined && isMobileReadyRoute(item.route) && (
                    <BluemateBadge
                        label={<AwesomeIcon name={'mobile-screen-button' as IconName} />}
                        color='green'
                        size='small'
                        className='mobile-ready-badge'
                        tooltip={t('sidebarMenu.mobileFriendly', 'Deze pagina werkt goed op mobiel')}
                    />
                )}
            </label>
        );
    }

    function renderChildren(isActive: boolean) {
        return function (children: BluemateSidebarItem[]) {
            const display = isActive ? 'block' : 'none';
            return (
                <div className='children-container' style={{display}}>
                    {children.map(renderItem(true))}
                </div>
            );
        };
    }

    function renderIcon(
        isActive: boolean,
        isChild: boolean,
        label: string,
        icon?: IconName | string | ReactElement<any>
    ): ReactNode {
        const className = classNames('item-icon', isActive && 'active');
        if (icon) {
            if (typeof icon === 'string') return <AwesomeIcon name={icon as IconName} className={className} />;
            else if (React.isValidElement<any>(icon))
                return React.cloneElement(icon, {className: classNames(className, icon.props.className)});
        }
        if (label.length) {
            if (isChild) return <span />;
            return <span className={classNames(className, 'letter')}>{label[0]}</span>;
        }
        return null;
    }

    return (
        <div className={classNames('bluemate-side-bar-menu', 'full', menuCollapsed && 'collapsed')} ref={sidebarRef}>
            <div className='menu-items-container'>{items.map(renderItem())}</div>
        </div>
    );
}
